import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Unsubscribe } from 'firebase/firestore';

import { CHAT_PRODUCT_ID } from 'src/app/config/product';
import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { OrganizationService } from 'src/app/services/organization/organization.service';
import { ProductAccessService } from 'src/app/services/product-access/product-access.service';
import { sharedTopicKey, sharedTopicNewAt, stampSharedTopics } from './shared-topic-first-seen';

export interface SidebarChatItem {
  chatId: string;
  title: string;
  pinned: boolean;
  folderId: string | null;
  /** `createdAt` epoch-ms — the Chats "new" badge (NavSeenService). Absent while the server stamp is pending. */
  createdAtMs?: number;
  /** `updatedAt` epoch-ms — last activity (Topics index recent chats). */
  updatedAtMs?: number;
}

export interface SidebarChatFolderItem {
  folderId: string;
  name: string;
  /** Emoji picked in the folder header, when set. */
  icon?: string;
  /** Static SVG glyph resolved from the shortcode `icon` (chat's folder-icon convention). */
  iconUrl?: string;
  /** Set for a folder another member shared with this user (2026-09-18): the
   *  folder lives under THEIR account; opening it needs `?owner=`. */
  ownerUid?: string;
  /** Own folder: how many members it is shared with (the folder doc's `sharedWith`); absent when not shared. */
  sharedWithCount?: number;
  /** Own folder: `created_at`; a share: when it was shared with this user, else when this device first saw it — the Topics "new" badge. */
  createdAtMs?: number;
}

/**
 * One row of the rail's recency-mixed CHATS roster — exactly one of
 * `chat`/`folder` is set, discriminated by `kind`.
 */
export interface SidebarRosterItem {
  key: string;
  kind: 'chat' | 'folder';
  chat?: SidebarChatItem;
  folder?: SidebarChatFolderItem;
  /** Epoch-ms of the row's latest activity, driving the mixed sort. */
  lastUsed: number;
}

/**
 * Backs the left sidebar's CHATS section: the user's personal AI chats and
 * folders (the hosted Chat product surface — users/{uid}/chats|folders, NOT
 * agent conversations). Reads Firestore directly so the shell stays decoupled
 * from the lifted chat feature's services.
 *
 * The section renders one recency stream (`roster$`): pinned chats first,
 * then folders and folder-less chats interleaved by last activity. A folder's
 * recency is its newest chat within the listener window, falling back to the
 * folder doc's own timestamps. The icon rail windows the roster for display
 * (6 rows + Show more) — the service exposes everything the 100-chat listener
 * window can order.
 *
 * Only listens while the org's product access includes 'chat'; live
 * revocation tears the listeners down and empties the lists.
 */
/** The chats listener's window (most recent chats). */
const CHATS_WINDOW = 100;

@Injectable({ providedIn: 'root' })
export class ChatSidebarService {
  public readonly allowed$ = new BehaviorSubject<boolean | null>(null);
  /** The mixed CHATS roster (pinned chats, then folders+chats by recency). */
  public readonly roster$ = new BehaviorSubject<SidebarRosterItem[] | null>(null);
  /** Chats grouped by folder, for inline folder expansion in the rail. */
  public readonly folderChats$ = new BehaviorSubject<Record<string, SidebarChatItem[]>>({});
  /** True when the chats listener hit its 100-row window: `folderChats$` then
   *  holds only each folder's most recent chats, so counts are a lower bound.
   *  False = the window holds every chat; null until its first snapshot. */
  public readonly chatWindowFull$ = new BehaviorSubject<boolean | null>(null);

  private started = false;
  private chatsSub: Unsubscribe | null = null;
  private foldersSub: Unsubscribe | null = null;
  private sharedFoldersSub: Unsubscribe | null = null;
  /** The org the live listeners were bound against (their visibility filter is org-scoped). */
  private boundOrgId: string | null = null;

  /** Latest snapshot state feeding `recomputeRoster()` — null until the
   * corresponding listener has delivered at least once. */
  private rootChatRows: SidebarRosterItem[] | null = null;
  private folderDocs: { item: SidebarChatFolderItem; docMs: number }[] | null = null;
  /** Folders shared with this user, from the functions-maintained mirror. */
  private sharedFolderDocs: { item: SidebarChatFolderItem; docMs: number }[] = [];
  private newestFolderChatMs: Record<string, number> = {};

  constructor(
    private firebase: FirebaseService,
    private organization: OrganizationService,
    private productAccess: ProductAccessService,
  ) {}

  /** Idempotent; call once auth + organization are ready (icon-rail pattern). */
  public start(): void {
    if (this.started) return;
    this.started = true;

    // The chats listener filters on the active org, so an in-place org switch
    // (OrgSwitcherService) must rebind it. Product access re-evaluates for the
    // new org too (allowedProducts$ below), but its timing is independent —
    // so handle both: drop the old org's rows here, listen again once allowed.
    this.organization.orgId$.subscribe((orgId) => {
      if (!this.boundOrgId || orgId === this.boundOrgId) return;
      this.teardown();
      this.roster$.next(null);
      this.folderChats$.next({});
      if (this.allowed$.value === true) {
        this.listen();
      }
    });

    this.productAccess.allowedProducts$.subscribe((products) => {
      if (products === null) return;
      const allowed = products.includes(CHAT_PRODUCT_ID);
      this.allowed$.next(allowed);
      if (allowed) {
        this.listen();
      } else {
        this.teardown();
        this.roster$.next([]);
        this.folderChats$.next({});
      }
    });
  }

  private listen(): void {
    if (this.chatsSub) return;
    const uid = this.firebase.auth.auth.currentUser?.uid;
    const orgId = this.organization.data?.id;
    if (!uid || !orgId) return;
    this.boundOrgId = orgId;

    const fs = this.firebase.firestore;

    this.chatsSub = fs.collectionListen(
      `users/${uid}/chats`,
      [fs.orderBy('updatedAt', 'desc'), fs.limit(CHATS_WINDOW)],
      (snap) => {
        const visible = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as any) }))
          // Same visibility rule as the chat product: current org (legacy
          // chats without a stamp stay visible), committed. Chats archived
          // before the archive feature was removed (2026-09-22) are ordinary
          // chats again — nothing is hidden; the user can delete them.
          .filter((c) => (!c.organizationId || c.organizationId === orgId)
            && c.provisional !== true);

        this.rootChatRows = visible
          .filter((c) => !c.folderId)
          .map((c): SidebarRosterItem => ({
            key: `chat:${c.id}`,
            kind: 'chat',
            chat: { chatId: c.id, title: c.title || '', pinned: !!c.pinned, folderId: null, createdAtMs: c.createdAt?.toMillis?.() ?? undefined },
            lastUsed: c.updatedAt?.toMillis?.() ?? 0,
          }));

        const grouped: Record<string, SidebarChatItem[]> = {};
        const newestMs: Record<string, number> = {};
        for (const c of visible) {
          if (!c.folderId) continue;
          (grouped[c.folderId] ??= []).push({
            chatId: c.id,
            title: c.title || '',
            pinned: !!c.pinned,
            folderId: c.folderId,
            updatedAtMs: c.updatedAt?.toMillis?.() ?? undefined,
          });
          newestMs[c.folderId] = Math.max(newestMs[c.folderId] ?? 0, c.updatedAt?.toMillis?.() ?? 0);
        }
        this.chatWindowFull$.next(snap.docs.length >= CHATS_WINDOW);
        this.folderChats$.next(grouped);
        this.newestFolderChatMs = newestMs;
        this.recomputeRoster();
      },
    );

    this.foldersSub = fs.collectionListen(
      `users/${uid}/folders`,
      [],
      (snap) => {
        this.folderDocs = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as any) }))
          .filter((f) => (!f.organizationId || f.organizationId === orgId) && !f.parent_id)
          .map((f) => ({
            item: {
              folderId: f.id,
              name: f.name || '',
              icon: f.meta?.icon,
              iconUrl: this.resolveIconUrl(f.meta?.icon),
              createdAtMs: f.created_at?.toMillis?.() ?? undefined,
              ...(Array.isArray(f.sharedWith) && f.sharedWith.length > 0 ? { sharedWithCount: f.sharedWith.length } : {}),
            } as SidebarChatFolderItem,
            docMs: Math.max(f.updated_at?.toMillis?.() ?? 0, f.created_at?.toMillis?.() ?? 0),
          }));
        this.recomputeRoster();
      },
    );

    // Folders other members shared with this user (Vincent, 2026-09-18):
    // listed from the `chat-folder-shares` mirror, since a member cannot read
    // another user's folder collection. Same row shape, tagged with the owner.
    this.sharedFoldersSub = fs.collectionListen(
      'chat-folder-shares',
      [fs.where('sharedWith', 'array-contains', uid)],
      (snap) => {
        const shares = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as any) }))
          .filter((f) => f.ownerUid && f.folderId && (!f.orgId || f.orgId === orgId));
        // Not the mirror's updatedAt: it moves on every owner edit (review S7).
        const firstSeen = stampSharedTopics(uid, orgId, shares.map((f) => sharedTopicKey(f.ownerUid, f.folderId)), snap.metadata?.fromCache === true);
        this.sharedFolderDocs = shares
          .map((f) => ({
            item: {
              folderId: f.folderId,
              name: f.name || '',
              icon: f.icon || undefined,
              iconUrl: this.resolveIconUrl(f.icon || undefined),
              ownerUid: f.ownerUid,
              createdAtMs: sharedTopicNewAt(f.sharedAt, uid, firstSeen[sharedTopicKey(f.ownerUid, f.folderId)]),
            } as SidebarChatFolderItem,
            docMs: f.updatedAt?.toMillis?.() ?? 0,
          }));
        this.recomputeRoster();
      },
      (error) => console.error('[ChatSidebar] shared folders listener failed', error),
    );
  }

  /**
   * Folder icons persist SHORTCODES (chat's `meta.icon` convention, e.g.
   * "rocket") that resolve to static SVG glyphs. The emoji dataset is lazy —
   * it belongs to the chat graph and must stay out of the eager bundle — so
   * icons resolve once it lands and the roster re-maps.
   */
  private shortCodeToEmoji: Record<string, string> | null = null;
  private loadingEmojiMap = false;

  private resolveIconUrl(icon: string | undefined): string | undefined {
    if (!icon) return undefined;
    if (!this.shortCodeToEmoji) {
      this.loadEmojiMap();
      return undefined;
    }
    const code = this.shortCodeToEmoji[icon];
    return code ? `/assets/emojis/${code.toLowerCase()}.svg` : undefined;
  }

  private loadEmojiMap(): void {
    if (this.loadingEmojiMap) return;
    this.loadingEmojiMap = true;
    void import('src/app/chat/core/services/emoji.service').then(({ EmojiService }) => {
      this.shortCodeToEmoji = new EmojiService().getShortCodeToEmoji();
      this.folderDocs = (this.folderDocs ?? []).map(({ item, docMs }) => ({
        item: { ...item, iconUrl: this.resolveIconUrl(item.icon) },
        docMs,
      }));
      // Shared topics carry shortcodes too (their mirror's `icon`).
      this.sharedFolderDocs = this.sharedFolderDocs.map(({ item, docMs }) => ({
        item: { ...item, iconUrl: this.resolveIconUrl(item.icon) },
        docMs,
      }));
      this.recomputeRoster();
    });
  }

  /**
   * Rebuild the mixed roster from the latest chat + folder snapshots: pinned
   * chats first (recency-ordered), then folders and folder-less chats
   * interleaved newest-first. A folder's recency is its newest chat within
   * the listener window, falling back to the folder doc's own timestamps so
   * a just-created empty folder surfaces at the top.
   */
  private recomputeRoster(): void {
    if (this.rootChatRows === null && this.folderDocs === null) return;
    const ownFolderIds = new Set((this.folderDocs ?? []).map(({ item }) => item.folderId));
    const folderRows: SidebarRosterItem[] = [
      ...(this.folderDocs ?? []),
      // A share never shadows an own folder of the same id (impossible in
      // practice — auto-ids — but keep the roster keys unique regardless).
      ...this.sharedFolderDocs.filter(({ item }) => !ownFolderIds.has(item.folderId)),
    ].map(({ item, docMs }) => ({
      key: `folder:${item.folderId}`,
      kind: 'folder',
      folder: item,
      lastUsed: Math.max(docMs, this.newestFolderChatMs[item.folderId] ?? 0),
    }));
    const chatRows = this.rootChatRows ?? [];
    const byRecency = (a: SidebarRosterItem, b: SidebarRosterItem) => b.lastUsed - a.lastUsed;
    const pinned = chatRows.filter((r) => r.chat!.pinned).sort(byRecency);
    const rest = [...folderRows, ...chatRows.filter((r) => !r.chat!.pinned)].sort(byRecency);
    this.roster$.next([...pinned, ...rest]);
  }

  /**
   * One-shot list of EVERY visible chat (same visibility rule as the listener,
   * without its 100-row window), newest first — the global search palette
   * matches titles against this so an old chat is as findable as a recent one.
   */
  public async listAllChats(): Promise<SidebarChatItem[]> {
    const uid = this.firebase.auth.auth.currentUser?.uid;
    const orgId = this.organization.data?.id;
    if (!uid || !orgId) return [];
    const fs = this.firebase.firestore;
    try {
      const snap = await fs.collectionGet(`users/${uid}/chats`, fs.orderBy('updatedAt', 'desc'));
      return snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as any) }))
        .filter((c) => (!c.organizationId || c.organizationId === orgId)
          && c.provisional !== true)
        .map((c): SidebarChatItem => ({ chatId: c.id, title: c.title || '', pinned: !!c.pinned, folderId: c.folderId ?? null }));
    } catch (err) {
      console.error('[ChatSidebar] Could not list chats for search', err);
      return [];
    }
  }

  /**
   * One-shot fetch of a folder's chats, on expand. The passive listener only
   * windows the 100 most-recent chats, so older foldered chats would
   * otherwise be invisible; this overrides the warm-cache grouping.
   */
  public async loadFolderChats(folderId: string): Promise<SidebarChatItem[] | null> {
    const uid = this.firebase.auth.auth.currentUser?.uid;
    const orgId = this.organization.data?.id;
    if (!uid || !orgId) return null;
    const fs = this.firebase.firestore;
    try {
      const snap = await fs.collectionGet(
        `users/${uid}/chats`,
        fs.where('folderId', '==', folderId),
      );
      const chats = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as any) }))
        .filter((c) => (!c.organizationId || c.organizationId === orgId)
          && c.provisional !== true)
        .sort((a, b) => (b.updatedAt?.toMillis?.() ?? 0) - (a.updatedAt?.toMillis?.() ?? 0))
        .sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned))
        .map((c): SidebarChatItem => ({
          chatId: c.id,
          title: c.title || '',
          pinned: !!c.pinned,
          folderId,
          updatedAtMs: c.updatedAt?.toMillis?.() ?? undefined,
        }));
      this.folderChats$.next({ ...this.folderChats$.getValue(), [folderId]: chats });
      return chats;
    } catch (err) {
      console.error('[ChatSidebar] Could not load folder chats', err);
      return null;
    }
  }

  private teardown(): void {
    this.chatsSub?.();
    this.foldersSub?.();
    this.sharedFoldersSub?.();
    this.sharedFoldersSub = null;
    this.sharedFolderDocs = [];
    this.chatsSub = null;
    this.foldersSub = null;
    this.boundOrgId = null;
    this.rootChatRows = null;
    this.folderDocs = null;
    this.newestFolderChatMs = {};
    this.chatWindowFull$.next(null);
  }
}
